import { useState } from "react";
import { Send, KeyRound, LogOut, CheckCircle2, Loader2, ChevronRight, Eye, EyeOff, Globe, Mail } from "lucide-react";
import { C, RED } from "../../theme/tokens";
import { Chip } from "../../components/common/primitives";
import { openExternalLink } from "../../utils/video";
import { buildTelegramLinkUrl, getRawInitData } from "../../utils/telegram";
import { LANGS, LANG_LABEL } from "../../i18n/dict";

const rowBase = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  textAlign: "left",
};

const iconChip = (tint) => ({
  flex: "0 0 auto",
  width: 38,
  height: 38,
  borderRadius: 12,
  background: tint === "green" ? C.deep : tint === "red" ? "#2A1712" : C.violetDeep,
  border: `1px solid ${tint === "green" ? C.green : tint === "red" ? RED : C.violet}55`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

/* Small rounded status badge — replaces plain subtitle text for
   states that matter at a glance (linked/not linked, set/not set),
   so scanning the whole settings card top-to-bottom tells you your
   account's security posture without reading every line. */
function StatusPill({ tone, children }) {
  const palette =
    tone === "green"
      ? { bg: `${C.greenBright}1f`, border: `${C.greenBright}55`, color: C.greenBright }
      : { bg: `${C.violetBright}1f`, border: `${C.violetBright}55`, color: C.violetBright };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 9px",
        borderRadius: 999,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.color,
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        fontSize: 10.5,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

/* Wraps expandable row content in a 0fr/1fr grid track instead of
   toggling display or measuring pixel heights by hand — animates
   smoothly on open/close purely in CSS, no JS height calc, no jump
   when the content's own height changes (e.g. an error line appearing). */
function Expandable({ open, children }) {
  return (
    <div style={{ display: "grid", gridTemplateRows: open ? "1fr" : "0fr", transition: "grid-template-rows .22s ease" }}>
      <div style={{ overflow: "hidden" }}>{children}</div>
    </div>
  );
}

const inputStyle = (hasError) => ({
  width: "100%",
  background: C.panel2,
  border: `1px solid ${hasError ? RED : C.hairline}`,
  borderRadius: 12,
  color: C.primary,
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  padding: "12px 40px 12px 13px",
  outline: "none",
  marginBottom: 10,
});

/* Small password field with its own show/hide toggle — reused three
   times in the change-password form below, so it earns being a
   subcomponent instead of copy-pasted per field. */
function PasswordField({ value, onChange, placeholder, hasError, autoComplete }) {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        style={inputStyle(hasError)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        style={{
          position: "absolute",
          right: 10,
          top: 12,
          border: "none",
          background: "transparent",
          color: C.mutedB,
          cursor: "pointer",
          padding: 2,
        }}
        aria-label={visible ? "hide" : "show"}
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

/* Language row: same list-row shell as the ones below it, so switching
   UI language reads as one more setting rather than a visually
   unrelated control bolted above the security card. */
function LanguageRow({ t, lang, setLang }) {
  return (
    <div style={{ ...rowBase, cursor: "default", flexWrap: "wrap", rowGap: 10 }}>
      <div style={iconChip("violet")}>
        <Globe size={16} color={C.violetBright} />
      </div>
      <div style={{ flex: "1 1 140px", minWidth: 0 }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14.5, color: C.primary }}>
          {t.prof_lang}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
        {LANGS.map((l) => (
          <Chip key={l} active={lang === l} onClick={() => setLang(l)}>
            {LANG_LABEL[l]}
          </Chip>
        ))}
      </div>
    </div>
  );
}

/* Telegram row: shown when opened in browser (outside Telegram WebApp).
   Fetches OTP via GET /api/user/link-telegram and opens the bot deep link. */
function TelegramRow({ t, user, requestTelegramLinkCode }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const linked = Boolean(user?.telegram_linked ?? user?.telegram_id);

  const handleClick = async () => {
    if (linked || busy) return;
    setError(false);
    setBusy(true);
    try {
      if (typeof requestTelegramLinkCode === "function") {
        const res = await requestTelegramLinkCode();
        const otp = res?.otp ?? res?.code;
        if (otp) {
          openExternalLink(buildTelegramLinkUrl(otp));
        } else {
          setError(true);
        }
      }
    } catch (e) {
      setError(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={handleClick} disabled={linked} style={{ ...rowBase, cursor: linked ? "default" : "pointer" }}>
      <div style={iconChip(linked ? "green" : "violet")}>
        {linked ? <CheckCircle2 size={17} color={C.greenBright} /> : <Send size={17} color={C.violetBright} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14.5, color: C.primary }}>
          {linked ? t.prof_tg_linked_title : t.prof_tg_link_title}
        </div>
        <div style={{ fontSize: 12, color: error ? RED : C.mutedB, marginTop: 3 }}>
          {error ? t.prof_tg_link_error : linked ? (user?.username ? `@${user.username}` : t.prof_tg_linked_sub) : t.prof_tg_link_sub}
        </div>
      </div>
      {!linked && (busy ? <Loader2 size={16} color={C.mutedB} style={{ animation: "pjSpin 1s linear infinite" }} /> : <ChevronRight size={16} color={C.mutedB} />)}
    </button>
  );
}

/* Email row: shown when opened inside Telegram WebApp (user is already on TG).
   If email is linked -> displays email + active pill.
   If not linked -> expands into an inline form to link an email address. */
function EmailRow({ t, user, linkEmail }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const linked = Boolean(user?.email);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = emailValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (typeof linkEmail === "function") {
        await linkEmail(email.trim().toLowerCase());
      }
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setOpen(false);
      }, 1400);
    } catch (e) {
      setError(e?.code === "email_invalid" ? "taken" : "generic");
    } finally {
      setSubmitting(false);
    }
  };

  if (linked) {
    return (
      <div style={{ ...rowBase, cursor: "default" }}>
        <div style={iconChip("green")}>
          <CheckCircle2 size={17} color={C.greenBright} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14.5, color: C.primary }}>
            {t.prof_email_linked_title}
          </div>
          <div style={{ fontSize: 12, color: C.mutedB, marginTop: 3 }}>
            {user.email}
          </div>
        </div>
        <StatusPill tone="green">
          {t.prof_status_active}
        </StatusPill>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} style={rowBase}>
        <div style={iconChip("violet")}>
          <Mail size={17} color={C.violetBright} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14.5, color: C.primary }}>
              {t.prof_email_link_title}
            </div>
            <StatusPill tone="violet">
              {t.prof_status_not_set}
            </StatusPill>
          </div>
          <div style={{ fontSize: 12, color: C.mutedB, marginTop: 3 }}>
            {t.prof_email_link_sub}
          </div>
        </div>
        <ChevronRight
          size={16}
          color={C.mutedB}
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s ease" }}
        />
      </button>

      <Expandable open={open}>
        <div style={{ padding: "2px 16px 16px" }}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.prof_email_placeholder}
            style={inputStyle(Boolean(error))}
          />

          {error && (
            <div style={{ color: RED, fontSize: 12, marginBottom: 8 }}>
              {error === "taken" ? t.prof_email_taken : t.prof_email_error}
            </div>
          )}
          {success && (
            <div style={{ color: C.greenBright, fontSize: 12, marginBottom: 8 }}>
              {t.prof_email_success}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "12px 16px",
              borderRadius: 12,
              border: "none",
              background: !canSubmit
                ? C.mutedA
                : `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
              color: !canSubmit ? C.mutedB : "#0b0d13",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              cursor: !canSubmit ? "not-allowed" : "pointer",
            }}
          >
            {submitting && <Loader2 size={14} style={{ animation: "pjSpin 1s linear infinite" }} />}
            {t.prof_email_submit}
          </button>
        </div>
      </Expandable>
    </div>
  );
}

/* Password row: collapsed by default (just "Change password" / "Set
   password" depending on whether the account has one yet), expands
   into an inline form in-place rather than a modal — keeps everything
   on one scrollable profile page as requested. */
function PasswordRow({ t, user, setPassword }) {
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const hasPassword = Boolean(user?.has_password);
  const mismatch = confirmPw.length > 0 && newPw !== confirmPw;
  const isMatch = confirmPw.length > 0 && newPw === confirmPw;
  const canSubmit =
    newPw.length >= 8 && isMatch && (!hasPassword || currentPw.length > 0) && !submitting;

  const reset = () => {
    setCurrentPw("");
    setNewPw("");
    setConfirmPw("");
    setError(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await setPassword(hasPassword ? currentPw : null, newPw);
      setSuccess(true);
      reset();
      setTimeout(() => {
        setSuccess(false);
        setOpen(false);
      }, 1400);
    } catch (e) {
      setError(e?.detail === "wrong_password" ? "wrong_password" : "generic");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} style={rowBase}>
        <div style={iconChip("violet")}>
          <KeyRound size={17} color={C.violetBright} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 14.5, color: C.primary }}>
              {hasPassword ? t.prof_pw_change_title : t.prof_pw_set_title}
            </div>
            <StatusPill tone={hasPassword ? "green" : "violet"}>
              {hasPassword ? t.prof_status_active : t.prof_status_not_set}
            </StatusPill>
          </div>
          <div style={{ fontSize: 12, color: C.mutedB, marginTop: 3 }}>
            {hasPassword ? t.prof_pw_change_sub : t.prof_pw_set_sub}
          </div>
        </div>
        <ChevronRight
          size={16}
          color={C.mutedB}
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s ease" }}
        />
      </button>

      <Expandable open={open}>
        <div style={{ padding: "2px 16px 16px" }}>
          {hasPassword && (
            <PasswordField
              value={currentPw}
              onChange={setCurrentPw}
              placeholder={t.prof_pw_current_placeholder}
              hasError={error === "wrong_password"}
              autoComplete="current-password"
            />
          )}
          <PasswordField
            value={newPw}
            onChange={setNewPw}
            placeholder={t.prof_pw_new_placeholder}
            autoComplete="new-password"
          />
          <PasswordField
            value={confirmPw}
            onChange={setConfirmPw}
            placeholder={t.prof_pw_confirm_placeholder}
            hasError={mismatch}
            autoComplete="new-password"
          />

          {mismatch && (
            <div style={{ color: RED, fontSize: 12, marginBottom: 8 }}>{t.prof_pw_mismatch}</div>
          )}
          {error === "wrong_password" && (
            <div style={{ color: RED, fontSize: 12, marginBottom: 8 }}>{t.prof_pw_wrong_current}</div>
          )}
          {error === "generic" && (
            <div style={{ color: RED, fontSize: 12, marginBottom: 8 }}>{t.prof_pw_error_generic}</div>
          )}
          {success && (
            <div style={{ color: C.greenBright, fontSize: 12, marginBottom: 8 }}>{t.prof_pw_success}</div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "12px 16px",
              borderRadius: 12,
              border: "none",
              background: !canSubmit
                ? C.mutedA
                : `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
              color: !canSubmit ? C.mutedB : "#0b0d13",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              cursor: !canSubmit ? "not-allowed" : "pointer",
            }}
          >
            {submitting && <Loader2 size={14} style={{ animation: "pjSpin 1s linear infinite" }} />}
            {hasPassword ? t.prof_pw_change_submit : t.prof_pw_set_submit}
          </button>
        </div>
      </Expandable>
    </div>
  );
}

/* Consolidated "Settings" panel: language, Telegram/Email link status,
   password set/change, and logout — one card instead of a separate
   one-off language row above it, so every account-level control lives
   in a single predictable, scrollable list. */
export function AccountSecurityCard({
  t,
  lang,
  setLang,
  user,
  requestTelegramLinkCode,
  setPassword,
  linkEmail,
  onLogout,
}) {
  const isTg = Boolean(getRawInitData());

  return (
    <div>
      <div
        style={{
          position: "relative",
          clipPath: "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 0 100%)",
          background: C.panel1,
          border: `1px solid ${C.hairline}`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 20,
            height: 20,
            background: `linear-gradient(135deg, transparent 49%, ${C.violetBright}22 50%)`,
            pointerEvents: "none",
          }}
        />
        <LanguageRow t={t} lang={lang} setLang={setLang} />
        <div style={{ height: 1, background: C.hairline, margin: "0 16px" }} />
        {isTg ? (
          <EmailRow t={t} user={user} linkEmail={linkEmail} />
        ) : (
          <TelegramRow t={t} user={user} requestTelegramLinkCode={requestTelegramLinkCode} />
        )}
        <div style={{ height: 1, background: C.hairline, margin: "0 16px" }} />
        <PasswordRow t={t} user={user} setPassword={setPassword} />
      </div>

      {!isTg && (
        <button
          onClick={onLogout}
          style={{
            width: "100%",
            marginTop: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "13px 16px",
            borderRadius: 14,
            border: `1px solid ${C.hairline}`,
            background: "transparent",
            color: RED,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: 13.5,
            cursor: "pointer",
          }}
        >
          <LogOut size={15} />
          {t.prof_logout}
        </button>
      )}
    </div>
  );
}
